import { useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useGlobalStore } from '../../stores/globalStore';

// Icon mapping for different notification types
const getNotificationIcon = (type: 'success' | 'error' | 'warning' | 'info') => {
  switch (type) {
    case 'success':
      return <CheckCircleIcon className="h-5 w-5 text-white" />;
    case 'error':
      return <XCircleIcon className="h-5 w-5 text-white" />;
    case 'warning':
      return <ExclamationTriangleIcon className="h-5 w-5 text-white" />;
    case 'info':
      return <InformationCircleIcon className="h-5 w-5 text-white" />;
    default:
      return <InformationCircleIcon className="h-5 w-5 text-white" />;
  }
};



export function NotificationManager() {
  const { notifications, removeNotification } = useGlobalStore();

  // Auto-remove notifications with different durations based on type
  useEffect(() => {
    const timers = new Map<string, NodeJS.Timeout>();

    notifications.forEach(notification => {
      // Only create timer if it does not exist for this notification
      if (!timers.has(notification.id)) {
        // Success messages disappear quickly, errors stay longer
        const duration = notification.type === 'success' ? 3000 : 
                        notification.type === 'error' ? 5000 : 4000;
        
        const timer = setTimeout(() => {
          removeNotification(notification.id);
        }, duration);
        timers.set(notification.id, timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [notifications, removeNotification]);

  if (notifications.length === 0) {
    return null;
  }

  // Only show the last 3 notifications to prevent stacking
  const visibleNotifications = notifications.slice(-3);
  
  return (
    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999] space-y-3 max-w-md w-full px-4">
      {visibleNotifications.map(notification => (
        <div
          key={notification.id}
          className={`
            relative flex items-start gap-3 p-4 rounded-xl shadow-xl
            animate-in slide-in-from-top duration-300 ease-out
            ${notification.type === 'error' 
              ? 'bg-red-500/90 border border-red-400/50' 
              : notification.type === 'success'
              ? 'bg-green-500/90 border border-green-400/50'
              : notification.type === 'warning'
              ? 'bg-yellow-500/90 border border-yellow-400/50'
              : 'bg-blue-500/90 border border-blue-400/50'
            }
          `}
        >
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            {getNotificationIcon(notification.type)}
          </div>
          
          {/* Message */}
          <div className="flex-1 min-w-0 text-sm font-semibold leading-relaxed text-white drop-shadow-sm break-words">
            {notification.message}
          </div>
          
          {/* Close Button */}
          <button
            onClick={() => removeNotification(notification.id)}
            className="flex-shrink-0 p-1 rounded-md hover:bg-white/30 transition-all duration-200 opacity-70 hover:opacity-100 text-white"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
